import AIChatWidget from '@/components/aichat/AiChatWidget';
import PageHero from '@/components/landing/page-hero';
import InputError from '@/components/input-error';
import SeoHead from '@/components/seo/SeoHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import LandingLayout from '@/layouts/landing-layout';
import { useForm } from '@inertiajs/react';
import { CircleCheckBig, FileUp, Fingerprint, ShieldCheck } from 'lucide-react';
import { FormEvent } from 'react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

const categories = [
    'Financial Fraud',
    'Bribery',
    'Abuse of Authority',
    'Harassment',
    'Conflict of Interest',
    'Academic Misconduct',
    'Procurement Issues',
    'Data Privacy Violation',
    'Other',
] as const;

interface SubmissionResult {
    tracking_number: string;
    secret_pin: string;
    status: string;
    date_updated: string;
}

interface ReportFormData {
    reporter_type: 'anonymous' | 'identified';
    category: string;
    incident_date: string;
    location: string;
    description: string;
    evidence: File | null;
    name: string;
    email: string;
    phone: string;
    certify: boolean;
}

export default function GovernanceIntegrityReport({ submissionResult }: { submissionResult?: SubmissionResult | null }) {
    const form = useForm<ReportFormData>({
        reporter_type: 'anonymous',
        category: '',
        incident_date: '',
        location: '',
        description: '',
        evidence: null,
        name: '',
        email: '',
        phone: '',
        certify: false,
    });

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        form.transform((data) => ({
            ...data,
            name: data.reporter_type === 'anonymous' ? '' : data.name,
            email: data.reporter_type === 'anonymous' ? '' : data.email,
            phone: data.reporter_type === 'anonymous' ? '' : data.phone,
        }));

        form.post('/governance/integrity/report', {
            forceFormData: true,
            preserveScroll: true,
            onSuccess: () => {
                form.reset();
            },
        });
    };

    return (
        <LandingLayout>
            <SeoHead
                title="Report a Concern"
                description="Submit a public integrity report to Northeastern College without creating an account."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                <PageHero
                    title="Report a Concern"
                    description="Submit a report in good faith using the public Integrity & Whistleblowing System. No login or account creation is required."
                    breadcrumbs={[
                        { label: 'Home', href: '/' },
                        { label: 'About' },
                        { label: 'Governance', href: '/governance' },
                        { label: 'Integrity & Whistleblowing System', href: '/governance/integrity' },
                        { label: 'Report a Concern' },
                    ]}
                    image="/images/hero/about.png"
                    imageAlt="Public report submission"
                    icon={ShieldCheck}
                />

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl space-y-8 px-4">
                    {submissionResult ? (
                        <Card className="border-border rounded-2xl py-0 shadow-sm">
                            <CardHeader className="border-b border-border/60 p-6">
                                <div className="flex items-start gap-3">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0A7A35]/10 text-[#0A7A35] dark:text-[#21d36b]">
                                        <CircleCheckBig className="h-6 w-6" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-xl font-bold">Report submitted successfully</CardTitle>
                                        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                                            Save the tracking number and secret PIN below. You will need both to check the public status of your report.
                                        </p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="grid gap-4 p-6 md:grid-cols-3">
                                <div className="bg-muted/40 border-border rounded-2xl border p-4">
                                    <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Tracking Number</div>
                                    <div className="text-foreground mt-2 text-lg font-bold">{submissionResult.tracking_number}</div>
                                </div>
                                <div className="bg-muted/40 border-border rounded-2xl border p-4">
                                    <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Secret PIN</div>
                                    <div className="text-foreground mt-2 text-lg font-bold">{submissionResult.secret_pin}</div>
                                </div>
                                <div className="bg-muted/40 border-border rounded-2xl border p-4">
                                    <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">Current Status</div>
                                    <div className="text-foreground mt-2 text-lg font-bold">{submissionResult.status}</div>
                                    <div className="text-muted-foreground mt-1 text-xs">Updated {submissionResult.date_updated}</div>
                                </div>
                            </CardContent>
                        </Card>
                    ) : null}

                    <div className="grid gap-8 xl:grid-cols-[1.1fr_0.9fr]">
                        <Card className="border-border rounded-2xl py-0 shadow-sm">
                            <CardHeader className="border-b border-border/60 p-6">
                                <CardTitle className="text-2xl font-bold">Public Report Submission</CardTitle>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Complete the form below. Fields for name, email, and phone remain optional except for identified reports, where
                                    your name is required.
                                </p>
                            </CardHeader>
                            <CardContent className="p-6">
                                <form onSubmit={submit} className="space-y-6">
                                    <fieldset className="space-y-3">
                                        <legend className="text-foreground text-sm font-bold">Report type</legend>
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            {[
                                                { value: 'anonymous', title: 'Anonymous Report', description: 'Submit without displaying your identity.' },
                                                { value: 'identified', title: 'Identified Report', description: 'Share your name while keeping contact fields optional.' },
                                            ].map((option) => (
                                                <label
                                                    key={option.value}
                                                    className={`border-border flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-all ${
                                                        form.data.reporter_type === option.value ? 'border-[#0A7A35] bg-[#0A7A35]/6' : 'hover:border-emerald-500/20'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="reporter_type"
                                                        value={option.value}
                                                        checked={form.data.reporter_type === option.value}
                                                        onChange={(event) => form.setData('reporter_type', event.target.value as ReportFormData['reporter_type'])}
                                                        className="mt-1 h-4 w-4 border-[#0A7A35] text-[#0A7A35] focus:ring-[#0A7A35]"
                                                    />
                                                    <span>
                                                        <span className="text-foreground block text-sm font-bold">{option.title}</span>
                                                        <span className="text-muted-foreground mt-1 block text-sm leading-relaxed">{option.description}</span>
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        <InputError message={form.errors.reporter_type} />
                                    </fieldset>

                                    <div className="grid gap-5 md:grid-cols-2">
                                        <div className="space-y-2">
                                            <Label htmlFor="category">Category</Label>
                                            <select
                                                id="category"
                                                value={form.data.category}
                                                onChange={(event) => form.setData('category', event.target.value)}
                                                className="border-input bg-background focus-visible:border-ring focus-visible:ring-ring/50 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-[3px]"
                                            >
                                                <option value="">Select a category</option>
                                                {categories.map((category) => (
                                                    <option key={category} value={category}>
                                                        {category}
                                                    </option>
                                                ))}
                                            </select>
                                            <InputError message={form.errors.category} />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="incident_date">Incident Date</Label>
                                            <Input
                                                id="incident_date"
                                                type="date"
                                                value={form.data.incident_date}
                                                onChange={(event) => form.setData('incident_date', event.target.value)}
                                            />
                                            <InputError message={form.errors.incident_date} />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="location">Location</Label>
                                        <Input id="location" value={form.data.location} onChange={(event) => form.setData('location', event.target.value)} />
                                        <InputError message={form.errors.location} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="description">Description</Label>
                                        <Textarea
                                            id="description"
                                            value={form.data.description}
                                            onChange={(event) => form.setData('description', event.target.value)}
                                            className="min-h-40"
                                            placeholder="Describe what happened, who was involved, and any details that may help an investigator review the concern."
                                        />
                                        <InputError message={form.errors.description} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="evidence">Upload Evidence</Label>
                                        <div className="border-border bg-muted/30 rounded-2xl border border-dashed p-4">
                                            <div className="flex items-start gap-3">
                                                <FileUp className="mt-1 h-5 w-5 text-[#0A7A35]" aria-hidden="true" />
                                                <div className="w-full">
                                                    <Input
                                                        id="evidence"
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                        onChange={(event) => form.setData('evidence', event.target.files?.[0] ?? null)}
                                                    />
                                                    <p className="text-muted-foreground mt-2 text-xs">
                                                        Optional. Accepted file types: PDF, JPG, PNG, DOC, DOCX. Maximum file size: 10MB.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <InputError message={form.errors.evidence} />
                                    </div>

                                    <div className="grid gap-5 md:grid-cols-2">
                                        {form.data.reporter_type === 'identified' ? (
                                            <>
                                                <div className="space-y-2 md:col-span-2">
                                                    <Label htmlFor="name">Name</Label>
                                                    <Input id="name" value={form.data.name} onChange={(event) => form.setData('name', event.target.value)} />
                                                    <InputError message={form.errors.name} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="email">Email</Label>
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        value={form.data.email}
                                                        onChange={(event) => form.setData('email', event.target.value)}
                                                    />
                                                    <InputError message={form.errors.email} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="phone">Phone</Label>
                                                    <Input id="phone" value={form.data.phone} onChange={(event) => form.setData('phone', event.target.value)} />
                                                    <InputError message={form.errors.phone} />
                                                </div>
                                            </>
                                        ) : (
                                            <div className="bg-muted/40 border-border rounded-2xl border p-4 text-sm leading-relaxed md:col-span-2">
                                                <div className="flex items-start gap-3">
                                                    <Fingerprint className="mt-0.5 h-5 w-5 text-[#0A7A35]" aria-hidden="true" />
                                                    <p className="text-muted-foreground">
                                                        You selected an anonymous report. Contact details are removed from the submission so the report
                                                        can be filed without identifying information.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        <label className="flex items-start gap-3 rounded-2xl border border-[#0A7A35]/15 bg-[#0A7A35]/6 p-4">
                                            <Checkbox checked={form.data.certify} onCheckedChange={(checked) => form.setData('certify', checked === true)} />
                                            <span className="text-sm leading-relaxed">
                                                I certify that this report is submitted in good faith.
                                            </span>
                                        </label>
                                        <InputError message={form.errors.certify} />
                                    </div>

                                    <Button type="submit" disabled={form.processing} className="rounded-full bg-[#00963f] px-6 text-white hover:bg-[#008538]">
                                        {form.processing ? 'Submitting...' : 'Submit Report'}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>

                        <div className="space-y-6">
                            <Card className="border-border rounded-2xl py-0 shadow-sm">
                                <CardHeader className="p-6">
                                    <CardTitle className="text-xl font-bold">What happens next</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4 p-6 pt-0">
                                    {[
                                        'A tracking number and 6-digit secret PIN are generated after successful submission.',
                                        'Use those credentials on the tracking page to view the current public status of your report.',
                                        'Internal notes and confidential case handling details are never shown on the public tracker.',
                                    ].map((item) => (
                                        <div key={item} className="flex items-start gap-3">
                                            <span className="mt-2 h-2 w-2 rounded-full bg-[#0A7A35]" />
                                            <p className="text-muted-foreground text-sm leading-relaxed">{item}</p>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>

                            <Card className="border-border rounded-2xl py-0 shadow-sm">
                                <CardHeader className="p-6">
                                    <CardTitle className="text-xl font-bold">Keep your tracking credentials secure</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3 p-6 pt-0 text-sm">
                                    <div className="bg-muted/40 border-border rounded-2xl border p-4">
                                        <p className="text-muted-foreground leading-relaxed">
                                            After submission, the system issues a unique tracking number and a 6-digit secret PIN. These credentials are
                                            required to view report updates through the public tracking page.
                                        </p>
                                    </div>
                                    <div className="bg-muted/40 border-border rounded-2xl border p-4">
                                        <p className="text-muted-foreground leading-relaxed">
                                            For confidentiality, keep both credentials private and store them in a safe place. Northeastern College does
                                            not display internal case notes in the public tracker.
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
